import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnInit,
  QueryList,
  ViewChildren,
  inject,
} from '@angular/core';
import { BehaviorSubject, merge, Observable, of, switchMap } from 'rxjs';
import { delay, take, tap, withLatestFrom } from 'rxjs/operators';
import {
  BoelsPointOfService,
  GeoPoint,
  StoreFinderSearchPage,
} from '@boels/models';
import { SiteContextFacade } from '@boels/shared-state';
import { BoelsGoogleMapsComponent } from '../../boels-google-maps/boels-google-maps.component';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { BreakpointService } from '@boels/helpers';
import { SpinnerComponent } from '../../spinner/spinner.component';
import { faExternalLinkSquareAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { StoreFinderListItemComponent } from './components/store-finder-list-item/store-finder-list-item.component';
import { ExtendedStoreFinderSearchPage } from './models/store-finder-list-item.models';
import { VerticalAlignerComponent } from '../../vertical-aligner/vertical-aligner.component';

@Component({
  selector: 'boels-store-finder-list-map',
  templateUrl: './store-finder-list-map.component.html',
  styleUrls: ['./store-finder-list-map.component.scss'],
  imports: [
    CommonModule,
    BoelsGoogleMapsComponent,
    TranslocoModule,
    SpinnerComponent,
    FontAwesomeModule,
    StoreFinderListItemComponent,
    VerticalAlignerComponent,
  ],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['storeFinder'],
    },
  ],
})
export class StoreFinderListMapComponent implements OnInit {
  private breakPointService = inject(BreakpointService);
  private siteContextFacade = inject(SiteContextFacade);

  @Input()
  stores$: Observable<ExtendedStoreFinderSearchPage>;

  @Input()
  isRegion?: boolean;

  region?: string;

  @ViewChildren('storeItem') storeItemList: QueryList<ElementRef>;

  active$: BehaviorSubject<BoelsPointOfService | undefined> =
    new BehaviorSubject<BoelsPointOfService | undefined>(undefined);

  mdAndBelow$: Observable<boolean> = this.breakPointService.mdAndBelow$;

  mapCenter: GeoPoint;

  faExternalLinkAlt = faExternalLinkSquareAlt;

  ngOnInit(): void {
    if (this.stores$) {
      this.stores$ = this.stores$.pipe(
        withLatestFrom(this.siteContextFacade.getActiveBaseSiteEntity()),
        switchMap(([storeFinderSearchPage, activeBaseSite]) =>
          of({
            ...storeFinderSearchPage,
            stores: [
              ...(storeFinderSearchPage.stores?.map((store) => ({
                ...store,
                showDetailsButton: store?.baseSites?.some(
                  (baseSite) => baseSite.uid === activeBaseSite.uid,
                ),
              })) ?? []),
            ],
          }),
        ),
        tap((storeFinderSearchPage: StoreFinderSearchPage) => {
          if (this.isRegion && storeFinderSearchPage.stores?.length) {
            this.region =
              storeFinderSearchPage.stores?.[0].address?.region?.name;
          }
          this.setActiveAndCenterMap(storeFinderSearchPage.stores?.[0]);
        }),
      );
    }
  }

  setActive(pointOfService: BoelsPointOfService | undefined): void {
    this.active$.next(pointOfService);
    merge([this.active$, this.storeItemList?.changes])
      ?.pipe(
        delay(1),
        switchMap((changes) => changes),
        take(1),
        tap(() => {
          this.storeItemList
            .find((item: ElementRef) =>
              item.nativeElement.classList.contains(pointOfService?.name),
            )
            ?.nativeElement?.scrollIntoView?.({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center',
            });
        }),
      )
      .subscribe();
  }

  setActiveAndCenterMap(pointOfService: BoelsPointOfService | undefined): void {
    // Workaround for triggering an update when selecting the currently selected store
    this.mapCenter = {
      longitude: (pointOfService?.geoPoint?.longitude ?? 0) + 0.0000001,
      latitude: (pointOfService?.geoPoint?.latitude ?? 0) + 0.0000001,
    };

    this.setActive(pointOfService);
  }
}
